import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import i18n from 'simple-react-i18n'
import { useDispatch } from 'react-redux'
import { Card, CardContent, Grid } from '@mui/material'
import { ceil, chunk, groupBy, last, range } from 'lodash'
import { getColorFromPalette2 } from 'utils/ColorUtil'
import Checkbox from 'components/forms/Checkbox'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import DtoPluvioMeasure from 'pluviometry/dto/measures/DtoPluvioMeasure'
import { ALL } from 'pluviometry/constants/PluvioOptionConstant'
import { CIVIL_YEAR, HYDRO_YEAR, OTHER, PAST_YEARS } from 'alerting/constants/ChartFollowContants'
import { CardTitle } from 'components/card/NewCard'

const COLUMNS_COUNT = 4
const HYDRO_YEAR_START_MONTH = 9

const remapCivilYear = (yearMeasures, time, minDate, maxDate) => {
    const refDate = minDate ?? maxDate
    const nowYear = time === OTHER && refDate ? moment(refDate).year() : moment().year()
    return yearMeasures.map(m => ({ ...m, date: moment(m.date).year(nowYear).valueOf() }))
}

const remapHydroYear = (yearMeasures, minDate, maxDate) => yearMeasures.map(m => {
    const measureDate = moment(m.date)
    const isAfterOctober = measureDate.month() >= HYDRO_YEAR_START_MONTH
    const year = isAfterOctober ? moment(minDate).year() : moment(maxDate).year()
    return { ...m, date: moment(m.date).year(year).valueOf() }
})

const PluvioSuiviPastYearsTab = ({
    changeParent,
    id,
    dataType,
    cumulPluvio,
    time,
    minDate,
    maxDate,
    tab,
}) => {
    const dispatch = useDispatch()
    const [activeYears, setActiveYears] = useState([])
    const [groupedCivil, setGroupedCivil] = useState({})
    const [groupedHydro, setGroupedHydro] = useState({})
    // Le fetch principal du graphe est désormais borné à la fenêtre des onglets de
    // temps ; l'overlay "années précédentes" a besoin de TOUT l'historique, on le
    // recharge donc ici à la demande (uniquement quand l'onglet est actif) et on
    // refetch quand le type de données change.
    const [fullMeasures, setFullMeasures] = useState([])

    useEffect(() => {
        if (tab !== PAST_YEARS || !dataType) {
            return
        }
        dispatch(PluviometryAction.fetchPluvioChronicMeasures({
            stationId: id,
            dataType,
            groupFunc: cumulPluvio || ALL,
            chartMode: false,
        })).then(json => setFullMeasures((json || []).map(j => new DtoPluvioMeasure(j))))
    }, [tab, id, dataType, cumulPluvio, dispatch])

    const yearRange = useMemo(
        () => range(moment(fullMeasures[0]?.date).year(), moment(last(fullMeasures)?.date).year() + 1)
            .filter(y => y !== moment().year()),
        [fullMeasures],
    )

    useEffect(() => {
        setGroupedCivil(groupBy(fullMeasures, m => moment(m.date).year()))
        setGroupedHydro(groupBy(fullMeasures, m => {
            const measureDate = moment(m.date)
            const year = measureDate.year()
            const isAfterOctober = measureDate.month() >= HYDRO_YEAR_START_MONTH
            return isAfterOctober ? year : year - 1
        }))
    }, [fullMeasures])

    useEffect(() => {
        const isCivilLike = time === CIVIL_YEAR || time === OTHER
        const group = isCivilLike ? groupedCivil : (time === HYDRO_YEAR ? groupedHydro : null)
        if (!group) {
            changeParent([])
            return
        }
        const yearSeries = yearRange.flatMap((y, idx) => {
            if (!activeYears.includes(y)) {
                return []
            }
            const rawMeasures = group[y] || []
            const yearMeasures = isCivilLike
                ? remapCivilYear(rawMeasures, time, minDate, maxDate)
                : remapHydroYear(rawMeasures, minDate, maxDate)
            if (!yearMeasures.length) {
                return []
            }
            return [{
                measures: yearMeasures,
                name: y.toString(),
                connectNulls: false,
                showSymbol: false,
                color: getColorFromPalette2(idx),
            }]
        })
        changeParent(yearSeries)
    }, [yearRange, activeYears, time, minDate, maxDate, groupedCivil, groupedHydro])

    const changeYear = (y, v) => setActiveYears(v ? [...activeYears, y] : activeYears.filter(y2 => y2 !== y))

    if (!fullMeasures.length || tab !== PAST_YEARS) {
        return null
    }

    const yearCheckboxes = yearRange.map((y, idx) => (
        <Grid
            key={`year${y}`}
            container
            alignItems='center'
            sx={{ cursor: 'pointer' }}
            onClick={() => changeYear(y, !activeYears.includes(y))}
        >
            <Checkbox checked={activeYears.includes(y)} />
            <span>
                <span
                    className='arrests-level-panel'
                    style={{ color: getColorFromPalette2(idx), backgroundColor: getColorFromPalette2(idx) }}
                >
                    OO
                </span>
                {y}
            </span>
        </Grid>
    ))
    const groups = chunk(yearCheckboxes, ceil(yearCheckboxes.length / COLUMNS_COUNT) || 1)
    const allSelected = activeYears.length === yearRange.length

    const isValidTimeMode = time === CIVIL_YEAR || time === HYDRO_YEAR

    return (
        <Card className='margin-top-1'>
            <CardTitle title={i18n.previousYears} />
            <CardContent>
                <Grid container spacing={1} sx={{ padding: 1 }}>
                    <Grid
                        size={12}
                        sx={{
                            backgroundColor: 'rgba(255, 193, 7, 0.12)',
                            border: '1px solid rgba(255, 193, 7, 0.4)',
                            borderRadius: 1,
                            padding: 1,
                            fontSize: '0.85rem',
                            color: 'rgba(0,0,0,0.75)',
                        }}
                    >
                        {i18n.previousYearsTimeModeHint}
                    </Grid>
                    {isValidTimeMode && (
                        <>
                            <Grid size={12}>
                                <Grid
                                    container
                                    alignItems='center'
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => setActiveYears(allSelected ? [] : yearRange)}
                                >
                                    <Checkbox checked={allSelected} />
                                    {i18n.selectAll}
                                </Grid>
                            </Grid>
                            {range(COLUMNS_COUNT).map(colIdx => (
                                <Grid key={`col${colIdx}`} size={12 / COLUMNS_COUNT}>
                                    {groups[colIdx] || []}
                                </Grid>
                            ))}
                        </>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}

PluvioSuiviPastYearsTab.propTypes = {
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    changeParent: PropTypes.func,
    id: PropTypes.number,
    dataType: PropTypes.number,
    cumulPluvio: PropTypes.string,
    time: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tab: PropTypes.string,
}

export default PluvioSuiviPastYearsTab
